import React from 'react';
import { FaMusic } from 'react-icons/fa';
import { notificationTile } from '../../../_helpers/notifications.js';
import NotificationTile from '../NotificationTile.jsx';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { englishT } from '../../../locales/index.js';
import { englishFormat } from '../../../_helpers/formatters.js';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Compact relative time: 18m, 3h, 2d, then weekday, then short date. `t`
 * localizes the unit suffixes and `format` (the active locale's, from
 * `useLocale()`) the dates; English without them.
 */
export const formatRelativeTime = (date, now = new Date(), { t = englishT, format = englishFormat } = {}) => {
  const diff = Math.max(0, now - date);
  if (diff < HOUR) return t('home.relativeTime.minutes', { count: Math.max(1, Math.floor(diff / MINUTE)) });
  if (diff < DAY) return t('home.relativeTime.hours', { count: Math.floor(diff / HOUR) });
  if (diff < 7 * DAY) return t('home.relativeTime.days', { count: Math.floor(diff / DAY) });
  if (diff < 30 * DAY) return format.date(date, 'weekday');
  return format.date(date, 'monthDay');
};

/**
 * Row text: the catalog descriptor (`item.message`, `{ key, params }`) when the
 * item carries one, otherwise the pre-rendered `label`.
 */
const itemText = (item, t) => (item.message ? t(item.message.key, item.message.params) : item.label);

/**
 * The screen-reader-only "(unread)" after a row's text. The space is a text
 * node of its own, outside the hidden span, so every accessible-name
 * implementation keeps it. Browsers keep a space inside the span too, but
 * Testing Library's (dom-accessibility-api) trims each inline element's text
 * and would read the name as "…is live(unread)".
 */
const UnreadSuffix = ({ unread, t }) =>
  unread ? (
    <>
      {' '}
      <span className="listener-home-visually-hidden">{t('home.notifications.unread')}</span>
    </>
  ) : null;

const DateMedallion = ({ date, format }) => (
  <span className="listener-home-medallion" aria-hidden="true">
    <span className="listener-home-medallion-day">{format.date(date, 'weekday')}</span>
    <span className="listener-home-medallion-date">{date.getDate()}</span>
  </span>
);

/**
 * A feed row's tile (ENG-889): the song's cover art, the band image or the tip
 * amount, as the backend's `visual` says; a placeholder of the same size when
 * there is no image to show.
 */
const FeedCover = ({ item }) => (
  <NotificationTile
    tile={item.tile ?? notificationTile(item)}
    type={item.kind}
    className="listener-home-notification-cover"
    emptyClassName="listener-home-cover--empty"
    moneyClassName="listener-home-notification-cover--money"
  />
);

/**
 * Recent listener activity (Figma Z10 Notifications). Request and tip rows
 * are as they were; backend feed rows (ENG-26, carrying a `notificationId`
 * — live streams and, since ENG-382, any type the backend renders a title
 * for) lead with their visual (ENG-889: cover art, band image or tip
 * amount), mark unread ones, and open the backend's
 * destination through `onOpen(item)` when it has one.
 */
const HomeNotificationsCard = ({ items = [], newCount = 0, onSeeAll, onOpen, now = new Date() }) => {
  const { t, format } = useLocale();
  return (
    <section className="listener-home-card listener-home-notifications" aria-labelledby="listener-home-notifications-title" data-testid="home-notifications">
      <div className="listener-home-card-header">
        <div className="listener-home-notifications-heading">
          <h2 id="listener-home-notifications-title" className="listener-home-card-title listener-home-notifications-title">
            {t('home.notifications.title')}
          </h2>
          {newCount > 0 && <span className="listener-home-chip">{t('home.notifications.newCount', { count: newCount })}</span>}
        </div>
        <button type="button" className="listener-home-text-link listener-home-text-link--sm" onClick={onSeeAll}>
          {t('home.notifications.seeAll')}
        </button>
      </div>

      {items.length > 0 ? (
        <ul className="listener-home-notifications-list">
          {items.map((item) => {
            const isFeed = item.notificationId != null;
            const canOpen = isFeed && Boolean(onOpen) && Boolean(item.destination);
            const unread = isFeed && !item.read;
            return (
              <li
                key={item.id}
                className={`listener-home-notification${unread ? ' listener-home-notification--unread' : ''}`}
                data-read={isFeed ? String(!unread) : undefined}
              >
                {isFeed ? (
                  <FeedCover item={item} />
                ) : item.kind === 'tip' ? (
                  <DateMedallion date={item.at} format={format} />
                ) : (
                  <span className="listener-home-notification-cover listener-home-cover--empty" aria-hidden="true">
                    <FaMusic />
                  </span>
                )}
                {canOpen ? (
                  <button
                    type="button"
                    className="listener-home-notification-label listener-home-notification-link"
                    onClick={() => onOpen(item)}
                  >
                    {itemText(item, t)}
                    <UnreadSuffix unread={unread} t={t} />
                  </button>
                ) : (
                  <span className="listener-home-notification-label">
                    {itemText(item, t)}
                    <UnreadSuffix unread={unread} t={t} />
                  </span>
                )}
                <time className="listener-home-notification-time" dateTime={item.at.toISOString()}>
                  {formatRelativeTime(item.at, now, { t, format })}
                </time>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="listener-home-card-empty">{t('home.notifications.caughtUp')}</p>
      )}
    </section>
  );
};

export default HomeNotificationsCard;
